import React from 'react';

import Button from 'CommonComponents/Button';

import FilterIcon from 'Assets/icons/filter.svg';
import TasksIcon from 'Assets/icons/tasks.svg';

// Empty state for the Actions Summary table. Replaces the bare empty table body so a summary with
// no rows reads as intentional and says where actions actually come from — they are only ever
// raised inside an activity on the Project Plan, never from this tab.
//
// Two variants, for the same reason ProjectPlanEmptyState has three: a summary reaches zero rows
// two different ways and the useful thing to say differs completely.
//
//   empty     — no actions exist anywhere in the plan. Point at where they are raised.
//   filtered  — the owner filter excludes everything. The actions exist; the view is narrow.
//
// The heading is "No actions yet" rather than "No open actions": the endpoint applies no
// open/completed filter, so completed actions are returned and counted here too.
export const VARIANT_EMPTY = 'empty';
export const VARIANT_FILTERED = 'filtered';

const ICONS = {
    [VARIANT_EMPTY]: TasksIcon,
    [VARIANT_FILTERED]: FilterIcon,
};

const ActionSummaryEmptyState = ({ variant = VARIANT_EMPTY, onGoToProjectPlan, onClearFilters }) => {
    const Icon = ICONS[variant] || ICONS[VARIANT_EMPTY];

    const heading = {
        [VARIANT_EMPTY]: $t('No actions yet'),
        [VARIANT_FILTERED]: $t('No actions match your filters'),
    }[variant];

    const body = {
        [VARIANT_EMPTY]: $t(
            'Actions are raised within an activity on the Project Plan. Once added, they appear here with their owner, due date and parent activity.',
        ),
        [VARIANT_FILTERED]: $t('Every action in this summary is currently filtered out. Widen the owner search, or clear the filters to see them all.'),
    }[variant];

    return (
        // Normal flow with vertical padding, unlike the Project Plan and Roadmap empty states which
        // use `tw-absolute tw-inset-0` to fill a grid frame of a fixed height. This one replaces a
        // table inside `.table__card`, which has no height of its own, so absolute positioning
        // would collapse the card to nothing.
        //
        // role="status" so a screen reader is told the table came back empty. Without it the only
        // signal is a silent swap of the table for this block, which announces nothing.
        <div role="status" className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-12 tw-px-6">
            <div className="tw-flex tw-items-center tw-justify-center tw-h-14 tw-w-14 tw-rounded-full tw-bg-background tw-mb-4">
                <Icon className="tw-h-6 tw-w-6 tw-text-charcoal-50" aria-hidden="true" />
            </div>
            <h4 className="tw-text-base tw-font-semibold tw-text-charcoal tw-mb-1">{heading}</h4>
            <p className="tw-text-sm tw-text-charcoal-75 tw-max-w-sm">{body}</p>

            {/* Not gated on canManage: this only switches to a tab the user can already see, and a
                read-only user browsing the plan is a legitimate thing to do. */}
            {variant === VARIANT_EMPTY && (
                <Button variant="outline" className="tw-mt-4" onClick={onGoToProjectPlan}>
                    {$t('Go to Project Plan')}
                </Button>
            )}

            {variant === VARIANT_FILTERED && (
                <Button variant="outline" className="tw-mt-4" onClick={onClearFilters}>
                    {$t('Clear filters')}
                </Button>
            )}
        </div>
    );
};

export default ActionSummaryEmptyState;
